import {
  GROUP_CODE_MAX,
  type Airport,
  type AirportCapability,
  type AirportGroup,
  type Country,
  type CountryGroup,
  type GroupFields,
} from './types'

/**
 * Group membership.
 *
 * Two kinds of group, one shape. A **list** group holds its members outright,
 * which is what the operator makes. A **derived** group names a rule and is
 * recomputed from the tables every time it is read, which is what the
 * built-ins are — so DOM cannot drift away from the scope column, and a new
 * Vietnamese station joins DOM the moment it is added.
 *
 * Nothing here is a permission. A group is only a way of naming a set of
 * places once instead of eleven times; what may fly there is decided by the
 * capability matrices (see fleet.ts).
 */

/** Airports the group covers, as IATA codes. */
export function airportGroupMembers(
  group: GroupFields,
  airports: Airport[],
  capability: AirportCapability[],
): Set<string> {
  const [kind, argument] = splitRule(group.rule)

  if (kind === 'scope') {
    return new Set(
      airports.filter((row) => row.iata && row.scope === argument).map((row) => row.iata),
    )
  }
  if (kind === 'country') {
    const wanted = new Set(argument.split('+'))
    return new Set(
      airports.filter((row) => row.iata && wanted.has(row.countryCode)).map((row) => row.iata),
    )
  }
  if (kind === 'continent') {
    const wanted = new Set(argument.split('+'))
    return new Set(
      airports.filter((row) => row.iata && wanted.has(row.continent)).map((row) => row.iata),
    )
  }
  if (kind === 'capability') {
    // Stations that carry a rule of their own. Group rules are excluded on
    // purpose: otherwise this group would contain its own members.
    return new Set(
      capability.filter((rule) => rule.subjectKind === 'station').map((rule) => rule.subject),
    )
  }
  if (kind === 'not') {
    const excluded = new Set(argument.split('+'))
    return new Set(
      airports.filter((row) => row.iata && !excluded.has(row.countryCode)).map((row) => row.iata),
    )
  }
  return new Set(group.members)
}

/** Countries the group covers, as ISO codes. */
export function countryGroupMembers(
  group: GroupFields,
  countries: Country[],
  airports: Airport[],
): Set<string> {
  const [kind, argument] = splitRule(group.rule)

  if (kind === 'continent') {
    // The continent lives on the airport row, not the country row, so a
    // country reaches its region through its stations. A country with no
    // airport in the drop therefore reaches no region — reported, not hidden.
    const wanted = new Set(argument.split('+'))
    const out = new Set<string>()
    for (const row of airports) {
      if (row.countryCode && wanted.has(row.continent)) out.add(row.countryCode)
    }
    return out
  }
  if (kind === 'not') {
    const excluded = new Set(argument.split('+'))
    return new Set(countries.filter((row) => !excluded.has(row.code)).map((row) => row.code))
  }
  return new Set(group.members)
}

function splitRule(rule: string): [string, string] {
  if (!rule) return ['', '']
  const at = rule.indexOf(':')
  return at === -1 ? [rule, ''] : [rule.slice(0, at), rule.slice(at + 1)]
}

/**
 * Group code -> members, built once per screen.
 *
 * A resolver that walked the airport table per row per column would turn a
 * 8.672-row table into a scan per cell.
 */
export function airportGroupIndex(
  groups: AirportGroup[],
  airports: Airport[],
  capability: AirportCapability[],
): Map<string, Set<string>> {
  return new Map(
    groups.map((group) => [group.code, airportGroupMembers(group, airports, capability)]),
  )
}

export function countryGroupIndex(
  groups: CountryGroup[],
  countries: Country[],
  airports: Airport[],
): Map<string, Set<string>> {
  return new Map(
    groups.map((group) => [group.code, countryGroupMembers(group, countries, airports)]),
  )
}

/** Groups containing one member, in table order. */
export function groupsContaining(
  code: string,
  groups: GroupFields[],
  index: Map<string, Set<string>>,
): GroupFields[] {
  return groups.filter((group) => index.get(group.code)?.has(code))
}

/**
 * Whether a limit value names a group rather than a place.
 *
 * The code spaces overlap, so the group wins where both exist and the build
 * script reports the collision. A tail whose LIMIT COUNTRY reads ITL means
 * "everywhere international", not a country nobody can find.
 */
export function limitTarget(
  value: string,
  index: Map<string, Set<string>>,
): { kind: 'group'; members: Set<string> } | { kind: 'code' } {
  const members = index.get(value)
  return members ? { kind: 'group', members } : { kind: 'code' }
}

/**
 * Format checks for a group the user is adding or editing.
 *
 * New codes may not collide with a station or country code. Capability rows
 * record their subject kind so they would stay unambiguous either way, but a
 * tail limit is a bare list of strings, and there a colliding code is read
 * as the group — so the collision is refused at the one place it can still
 * be avoided. The built-in DOM is the exception: it is the operator's own
 * word and predates the group table, and it happens to be Dominica's IATA
 * code too. The build script reports that pair rather than renaming it.
 */
export function groupCodeError(
  code: string,
  taken: { groups: Set<string>; places: Set<string> },
): string | null {
  const value = code.trim().toUpperCase()
  if (!value) return 'Required'
  if (value.length > GROUP_CODE_MAX) return `Up to ${GROUP_CODE_MAX} characters`
  if (!/^[A-Z0-9-]+$/.test(value)) return 'A-Z, 0-9 and -'
  if (taken.groups.has(value)) return 'Already in use'
  if (taken.places.has(value)) return 'Already a station or country code'
  return null
}
